import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from 'components/Button';
import {
    confirmSponsorApproval,
    fetchProfileBySlug,
    fetchSponsorProof,
    fetchStory,
    newsroomError,
    uploadSponsorLogo,
    withdrawSponsorApproval,
} from 'api/newsroom';
import {
    handleHidePopup,
    handleShowPopup,
    setPopupConfig,
    setPopupLoading,
} from 'store/ux';
import { formatDate } from 'utils/newsroom';
import {
    LOGO_MAX_BYTES,
    LOGO_TYPES,
    PROOF_MAX_BYTES,
    PROOF_TYPES,
    SPECIALISM_MAX,
    SPONSOR_NAME_MAX,
    fileProblem,
    httpUrl,
    isLegacySlot,
    liveSponsor,
    profileSlug,
    sponsorBannerKey,
    sponsorDraft,
    sponsorStarted,
    templateComplete,
    templateSponsor,
} from 'utils/story-sponsor';
import { SponsorBanner } from './StorySponsor';
import styles from 'styles/modules/newsAlert/Newsroom.module.scss';

const DATES = [
    ['sponsorStartsAt', 'Runs from'],
    ['sponsorEndsAt', 'Runs until'],
];
const PROOF_VIEW_TYPES = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/webp',
];

// Drawn at its real size so the admin reads it as visitors will; the narrow panel scrolls it.
const Preview = ({ width, children }) => (
    <div className={styles.sponsorPreviewBox} aria-hidden="true">
        <div className={styles.sponsorPreviewInner} style={{ width }}>
            {children}
        </div>
    </div>
);

const Counter = ({ value, max }) => (
    <span className={styles.count}>
        {value.length}/{max}
    </span>
);

const Notice = ({ notice }) =>
    notice && (
        <p
            className={notice.type === 'error' ? styles.error : styles.ok}
            role={notice.type === 'error' ? 'alert' : 'status'}
        >
            {notice.text}
        </p>
    );

// The sponsor signs off the banner before it runs; the API keeps the proof private.
const ApprovalBox = ({
    alertId,
    story,
    draft,
    saveLabel,
    onStoryChange,
    onReload,
}) => {
    const dispatch = useDispatch();
    const [confirmed, setConfirmed] = useState(false);
    const [proof, setProof] = useState(null);
    const [proofKey, setProofKey] = useState(0);
    const [busy, setBusy] = useState(null);
    const [notice, setNotice] = useState(null);
    const [cleared, setCleared] = useState(false);
    const previous = useRef(story);

    // The API clears the approval in the same save that changes the banner.
    useEffect(() => {
        const before = previous.current;
        previous.current = story;
        if (story?.sponsorApproval) setCleared(false);
        else if (
            before?.sponsorApproval &&
            sponsorBannerKey(before) !== sponsorBannerKey(story)
        ) {
            setCleared(true);
            setNotice(null);
        }
    }, [story]);

    // A success message describes the banner before this edit; an error (the 409) stays.
    const draftKey = sponsorBannerKey(draft);
    useEffect(() => {
        setNotice((current) => (current?.type === 'ok' ? null : current));
    }, [draftKey]);

    const approval = story?.sponsorApproval;
    const started = templateComplete(story) || templateComplete(draft);
    const unsaved = draftKey !== sponsorBannerKey(story);
    const waiting = started && !unsaved && !approval;
    const proofProblem = fileProblem(
        proof,
        PROOF_TYPES,
        PROOF_MAX_BYTES,
        'proof'
    );
    const save = `Press \u201c${saveLabel}\u201d below`;
    const status = !started
        ? `Fill in the banner and press \u201c${saveLabel}\u201d below, then confirm the sponsor\u2019s approval here. It is not shown on the page until then.`
        : unsaved
        ? `${save} first; the banner stays off the page until you confirm the sponsor\u2019s approval of this version here.`
        : approval
        ? `Approved by ${
              approval.approvedBy?.name ||
              approval.approvedBy?.email ||
              'an admin'
          } on ${formatDate(approval.approvedAt)}`
        : cleared
        ? 'The banner changed, so its approval was cleared. Not shown on the page until you confirm the sponsor\u2019s approval of this version.'
        : 'Not shown on the page until you confirm the sponsor\u2019s approval.';

    const run = async (key, action, done, fallback) => {
        setBusy(key);
        setNotice(null);
        try {
            const saved = await action();
            if (saved) onStoryChange(saved);
            if (done) setNotice({ type: 'ok', text: done });
        } catch (err) {
            setNotice({ type: 'error', text: newsroomError(err, fallback) });
        } finally {
            setBusy(null);
        }
    };

    const confirm = () =>
        run(
            'confirm',
            async () => {
                let saved;
                try {
                    saved = await confirmSponsorApproval(
                        alertId,
                        proof,
                        story.sponsorBannerHash
                    );
                } catch (err) {
                    // Someone else saved the banner: show theirs, never invite a save over it.
                    const fresh =
                        err?.response?.status === 409 &&
                        (await fetchStory(alertId).catch(() => null));
                    if (fresh) {
                        onReload(fresh);
                        setConfirmed(false);
                        setProof(null);
                        setProofKey((n) => n + 1);
                    }
                    throw err;
                }
                setConfirmed(false);
                setProof(null);
                setProofKey((n) => n + 1);
                return saved;
            },
            'Approval confirmed. The banner runs inside its dates.',
            'Could not confirm the approval.'
        );

    const withdraw = () => {
        dispatch(handleShowPopup());
        dispatch(
            setPopupConfig({
                title: 'Withdraw the approval?',
                body: 'The banner comes off the page until the sponsor approves it again.',
                confirmTitle: 'Withdraw approval',
                cancelTitle: 'Keep it',
                onCancel: () => dispatch(handleHidePopup()),
                onConfirm: async () => {
                    dispatch(setPopupLoading(true));
                    await run(
                        'withdraw',
                        () => withdrawSponsorApproval(alertId),
                        'Approval withdrawn. The banner is off the page.',
                        'Could not withdraw the approval.'
                    );
                    dispatch(setPopupLoading(false));
                    dispatch(handleHidePopup());
                },
            })
        );
    };

    const viewProof = () => {
        // Opened before the request, or the browser treats it as an unrequested pop-up.
        const tab = window.open('', '_blank');
        return run(
            'proof',
            async () => {
                try {
                    const proof = await fetchSponsorProof(alertId);
                    // A blob URL runs with this app's origin, so only types that can't carry script open inline.
                    const type = PROOF_VIEW_TYPES.includes(proof.type)
                        ? proof.type
                        : 'application/octet-stream';
                    const url = URL.createObjectURL(
                        new Blob([proof], { type })
                    );
                    if (tab) tab.location.href = url;
                    else window.location.assign(url);
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                } catch (err) {
                    tab?.close();
                    throw err;
                }
            },
            null,
            'Could not open the proof.'
        );
    };

    return (
        <div className={styles.sponsorApproval}>
            <span className={styles.label}>Sponsor approval</span>
            <p
                className={
                    !started
                        ? styles.hint
                        : approval && !unsaved
                        ? styles.sponsorApproved
                        : styles.faqStatus
                }
                role="status"
            >
                {status}
            </p>
            {approval && !unsaved && (
                <div className={styles.editorActions}>
                    {approval.hasProof && (
                        <button
                            type="button"
                            className={styles.linkButton}
                            disabled={Boolean(busy)}
                            onClick={viewProof}
                        >
                            {busy === 'proof'
                                ? 'Opening proof\u2026'
                                : 'View proof'}
                        </button>
                    )}
                    <Button
                        title={'Withdraw approval'}
                        theme={'secondary'}
                        loading={busy === 'withdraw'}
                        disabled={Boolean(busy)}
                        onClick={withdraw}
                    />
                </div>
            )}
            {waiting && (
                <>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={confirmed}
                            onChange={(e) => setConfirmed(e.target.checked)}
                        />
                        Sponsor approval for this banner ad confirmed
                    </label>
                    <label className={styles.label} htmlFor="sponsor-proof">
                        Upload proof of approval
                    </label>
                    <input
                        key={proofKey}
                        id="sponsor-proof"
                        className={styles.fileInput}
                        type="file"
                        accept={PROOF_TYPES.join(',')}
                        required
                        aria-describedby="sponsor-proof-hint"
                        onChange={(e) => setProof(e.target.files?.[0] ?? null)}
                    />
                    <p id="sponsor-proof-hint" className={styles.hint}>
                        A screenshot of the email or a PDF: PNG, JPEG, WebP or
                        PDF, up to 10 MB. Kept private.
                    </p>
                    {proofProblem && (
                        <p className={styles.overLimit} role="alert">
                            {proofProblem}
                        </p>
                    )}
                    <Button
                        title={'Confirm approval'}
                        theme={'success'}
                        loading={busy === 'confirm'}
                        disabled={
                            Boolean(busy) ||
                            !confirmed ||
                            !proof ||
                            Boolean(proofProblem)
                        }
                        onClick={confirm}
                    />
                </>
            )}
            <Notice notice={notice} />
        </div>
    );
};

// One sponsor per page: off while the alert offers a story research sponsorship,
// which the API refuses alongside it.
const StorySponsorFields = ({
    alertId,
    story,
    draft,
    saveLabel,
    onChange,
    onStoryChange,
    researchOn,
}) => {
    const dispatch = useDispatch();
    const [uploading, setUploading] = useState(false);
    const [logoError, setLogoError] = useState(null);
    const [profileInput, setProfileInput] = useState(
        story?.sponsorProfile?.slug ?? ''
    );
    const [found, setFound] = useState(null);
    const [lookup, setLookup] = useState({ busy: false, error: null });

    const profileId = draft.sponsorProfileId;
    const profile = !profileId
        ? null
        : [found, story?.sponsorProfile].find(
              (p) => p && String(p.id) === profileId
          ) ?? null;
    const typedSlug = profileSlug(profileInput);
    const preview = templateSponsor(
        {
            ...draft,
            sponsorName: draft.sponsorName || 'Sponsor name',
            sponsorSpecialisms: draft.sponsorSpecialisms.some((s) => s.trim())
                ? draft.sponsorSpecialisms
                : ['Specialism'],
        },
        Boolean(profile?.isVerified)
    );
    const thumb = httpUrl(draft.sponsorLogoUrl);

    const setFields = (fields) =>
        Object.entries(fields).forEach(([key, value]) => onChange(key, value));

    const resetProfile = (next) => {
        setProfileInput(next?.slug ?? '');
        setFound(null);
        setLookup({ busy: false, error: null });
    };

    const reload = (fresh) => {
        onStoryChange(fresh);
        setFields(sponsorDraft(fresh));
        resetProfile(fresh?.sponsorProfile);
    };

    const clearSlot = () => {
        setFields(sponsorDraft(null));
        resetProfile(null);
        setLogoError(null);
    };

    const handleRemove = () => {
        if (!story?.sponsorApproval && !liveSponsor(story)) {
            clearSlot();
            return;
        }
        dispatch(handleShowPopup());
        dispatch(
            setPopupConfig({
                title: 'Remove the sponsor?',
                body: `This banner is approved or live. It comes off the page when you press “${saveLabel}”.`,
                confirmTitle: 'Remove sponsor',
                cancelTitle: 'Keep it',
                onCancel: () => dispatch(handleHidePopup()),
                onConfirm: () => {
                    clearSlot();
                    dispatch(handleHidePopup());
                },
            })
        );
    };

    const handleLogo = async (e) => {
        const input = e.target;
        const file = input.files?.[0];
        if (!file) return;
        const problem = fileProblem(file, LOGO_TYPES, LOGO_MAX_BYTES, 'logo');
        setLogoError(problem);
        if (problem) {
            input.value = '';
            return;
        }
        setUploading(true);
        try {
            onChange('sponsorLogoUrl', await uploadSponsorLogo(alertId, file));
        } catch (err) {
            setLogoError(newsroomError(err, 'Could not upload the logo.'));
        } finally {
            setUploading(false);
            input.value = '';
        }
    };

    const findProfile = async () => {
        if (!typedSlug) {
            setLookup({
                busy: false,
                error: 'Enter the profile address or its slug.',
            });
            return;
        }
        setLookup({ busy: true, error: null });
        try {
            const result = await fetchProfileBySlug(typedSlug);
            if (!result?.id) {
                throw Object.assign(new Error('not found'), {
                    response: { status: 404 },
                });
            }
            setFound(result);
            onChange('sponsorProfileId', String(result.id));
            setLookup({ busy: false, error: null });
        } catch (err) {
            setLookup({
                busy: false,
                error:
                    err?.response?.status === 404
                        ? 'No Newspage profile at that address.'
                        : newsroomError(err, 'Could not look up the profile.'),
            });
        }
    };

    const setSpecialism = (index, value) =>
        onChange(
            'sponsorSpecialisms',
            draft.sponsorSpecialisms.map((s, i) => (i === index ? value : s))
        );

    return (
        <fieldset className={styles.faqOffer} disabled={researchOn}>
            <legend className={styles.kicker}>Sponsor slot</legend>
            <p className={styles.hint}>
                {researchOn
                    ? 'Not available: this alert offers a story research sponsorship, and the page carries one sponsor. Turn it off in Edit first.'
                    : 'One labelled ad above the headline (below the byline on phones), built from the fields below. Links in the story to the sponsor’s site are marked sponsored automatically. Leave the dates empty to run it from now with no end.'}
            </p>
            {isLegacySlot(story) && (
                <p className={styles.faqStatus}>
                    Old image banner &mdash; fill in the template fields to
                    replace it.
                </p>
            )}

            <label className={styles.label} htmlFor="story-sponsorName">
                Sponsor name{' '}
                <Counter value={draft.sponsorName} max={SPONSOR_NAME_MAX} />
            </label>
            <input
                id="story-sponsorName"
                className={styles.input}
                value={draft.sponsorName}
                maxLength={SPONSOR_NAME_MAX}
                placeholder="Check Financial"
                onChange={(e) => onChange('sponsorName', e.target.value)}
            />

            <label className={styles.label} htmlFor="story-sponsorLogo">
                Logo
            </label>
            {thumb && (
                <div className={styles.sponsorThumbRow}>
                    <span
                        className={[
                            styles.sponsorThumb,
                            draft.sponsorLogoBg === 'white'
                                ? styles.bannerLogoWhite
                                : '',
                        ].join(' ')}
                    >
                        <img src={thumb} alt="Current logo" />
                    </span>
                    <button
                        type="button"
                        className={styles.linkButton}
                        onClick={() => onChange('sponsorLogoUrl', '')}
                    >
                        Remove logo
                    </button>
                </div>
            )}
            <input
                id="story-sponsorLogo"
                className={styles.fileInput}
                type="file"
                accept={LOGO_TYPES.join(',')}
                disabled={uploading}
                aria-describedby="story-sponsorLogo-hint"
                onChange={handleLogo}
            />
            <p id="story-sponsorLogo-hint" className={styles.hint}>
                {uploading
                    ? 'Uploading…'
                    : `${
                          thumb ? 'Choose a file to replace it. ' : ''
                      }PNG, JPEG, SVG or WebP, up to 2 MB. A wide or square logo on a transparent background works best.`}
            </p>
            {logoError && (
                <p className={styles.overLimit} role="alert">
                    {logoError}
                </p>
            )}

            <fieldset className={styles.sponsorChoice}>
                <legend className={styles.label}>Logo background</legend>
                <div>
                    {[
                        ['navy', 'Navy'],
                        ['white', 'White'],
                    ].map(([value, label]) => (
                        <label key={value} className={styles.checkboxLabel}>
                            <input
                                type="radio"
                                name="story-sponsorLogoBg"
                                value={value}
                                checked={draft.sponsorLogoBg === value}
                                onChange={() =>
                                    onChange('sponsorLogoBg', value)
                                }
                            />
                            {label}
                        </label>
                    ))}
                </div>
            </fieldset>

            <fieldset className={styles.sponsorGroup}>
                <legend className={styles.label}>
                    Specialisms (1&ndash;3)
                </legend>
                {draft.sponsorSpecialisms.map((specialism, i) => (
                    <div key={i}>
                        <label
                            className={styles.sponsorSubLabel}
                            htmlFor={`story-sponsorSpecialism-${i}`}
                        >
                            {`Specialism ${i + 1} `}
                            <Counter value={specialism} max={SPECIALISM_MAX} />
                        </label>
                        <input
                            id={`story-sponsorSpecialism-${i}`}
                            className={styles.input}
                            value={specialism}
                            maxLength={SPECIALISM_MAX}
                            placeholder={
                                ['Mortgages', 'Protection', 'Equity release'][i]
                            }
                            onChange={(e) => setSpecialism(i, e.target.value)}
                        />
                    </div>
                ))}
            </fieldset>

            <label className={styles.label} htmlFor="story-sponsorUrl">
                Sponsor link
            </label>
            <input
                id="story-sponsorUrl"
                className={styles.input}
                type="url"
                value={draft.sponsorUrl}
                maxLength={1000}
                placeholder="https://…"
                onChange={(e) => onChange('sponsorUrl', e.target.value)}
            />

            <label className={styles.label} htmlFor="story-sponsorProfile">
                Sponsor&rsquo;s Newspage profile (optional)
            </label>
            <div className={styles.sponsorLookup}>
                <input
                    id="story-sponsorProfile"
                    className={styles.input}
                    value={profileInput}
                    placeholder="oliver-smith or its Newspage address"
                    aria-describedby="story-sponsorProfile-status"
                    onChange={(e) => {
                        setProfileInput(e.target.value);
                        setLookup({ busy: false, error: null });
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            findProfile();
                        }
                    }}
                />
                <Button
                    title={'Find'}
                    theme={'secondary'}
                    loading={lookup.busy}
                    onClick={findProfile}
                />
            </div>
            <p
                id="story-sponsorProfile-status"
                className={lookup.error ? styles.overLimit : styles.hint}
                role="status"
            >
                {lookup.error ||
                    (typedSlug && typedSlug !== (profile?.slug ?? '')
                        ? 'Press Find to link this profile.'
                        : profile
                        ? `${profile.name}: ${
                              profile.isVerified ? 'verified' : 'not verified'
                          }.`
                        : profileId
                        ? 'Linked to a profile.'
                        : 'No profile linked.')}
                {profileId && (
                    <>
                        {' '}
                        <button
                            type="button"
                            className={styles.linkButton}
                            onClick={() => {
                                onChange('sponsorProfileId', '');
                                resetProfile(null);
                            }}
                        >
                            Remove
                        </button>
                    </>
                )}
            </p>
            <p className={styles.hint}>
                {`Label: “${preview.label}”.${
                    profile?.isVerified
                        ? ''
                        : ' A verified profile gets “Newspage-verified expert”.'
                }`}
            </p>

            {DATES.map(([name, label]) => (
                <div key={name}>
                    <label className={styles.label} htmlFor={`story-${name}`}>
                        {label}
                    </label>
                    <input
                        id={`story-${name}`}
                        className={styles.input}
                        type="datetime-local"
                        value={draft[name]}
                        onChange={(e) => onChange(name, e.target.value)}
                    />
                </div>
            ))}

            <span className={styles.label}>Preview: desktop (728×90)</span>
            <Preview width={728}>
                <SponsorBanner sponsor={preview} variant="desktop" asPreview />
            </Preview>
            <span className={styles.label}>Preview: mobile (320×100)</span>
            <Preview width={320}>
                <SponsorBanner sponsor={preview} variant="mobile" asPreview />
            </Preview>

            {(sponsorStarted(draft) || draft.sponsorLogoUrl) && (
                <div className={styles.editorActions}>
                    <Button
                        title={'Remove sponsor'}
                        theme={'secondary'}
                        onClick={handleRemove}
                    />
                </div>
            )}

            <ApprovalBox
                alertId={alertId}
                story={story}
                draft={draft}
                saveLabel={saveLabel}
                onStoryChange={onStoryChange}
                onReload={reload}
            />
        </fieldset>
    );
};

export default StorySponsorFields;
